import { graphql } from "gatsby";
import { MDXRenderer } from "gatsby-plugin-mdx";
import PropTypes from "prop-types";
import React from "react";
import styled from "styled-components";
import Layout from "../components/layout";
import SEO from "../components/seo";
import ContentWrapper from "../styles/ContentWrapper";
import Theme from "../styles/Theme";
import Underlining from "../styles/Underlining";

const StyledSection = styled.section`
  width: 100%;
  max-width: 62.5rem;
  margin: 0 auto;
  padding: 0 2.5rem;
  height: auto;
  background: ${({ theme }) => theme.colors.background};
  h1 {
      font-size: 1.5rem;
  }
  h2 {
      font-size: 1.25rem;
  }
  h3 {
      font-size: 1rem;
      margin-bottom: 1rem;
  }
`

const StyledContentWrapper = styled(ContentWrapper)`
  && {
    width: 100%;
    margin: 0;
    padding: 0;
    height: 100%;
    h4 {
      font-size: 1rem;
      margin-bottom: 0.8rem;
    }
    .pub-section {
      margin-bottom: 2em;
    }
    .pub {
      margin-left: 1em;
      margin-bottom: 0.5em;
      padding-bottom: 0.5em;
      border-bottom: 1px #cbcbcb solid;
    }
    .pub-title {
      font-weight: bold;
    }
    .pub-count {
      font-weight: normal;
      font-size: small;
    }
    .pub-links {
      font-size: 90%;
    }
    .emph {
      color: ${({ theme }) => theme.colors.emphasize};
      font-weight: bold;
    }
  }
`

function lookup_pdf(name, pdfs) {
  // if pdf name is URL, return the URL
  if (name.match(/^http(|s):\/\//)) return name
  const link = pdfs.filter(a => a.node.name === name)
  if (link.length > 0) return link[0].node.publicURL
  return null
}

function sectionHandler(node, pdfs) {
  const month_str = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
  const sectionItems = [];
  // ignore english-only entries (which are copy of a japanese entry)
  const body = node.body.filter(a => a.lang !== 'en')
  if (body.length === 0)
    return null

  sectionItems.push(<h2 className="subhead">{node.title} <span className="pub-count">({body.length})</span></h2>)
  body.forEach(item => {
    // attribute items
    var attribItems = [];
    attribItems.push(item.in)
    if (item.vol_pp) attribItems.push(item.vol_pp)
    if (item.month) {
      const months = []
      item.month.split("-").forEach(m => {
        months.push(month_str[parseInt(m, 10) - 1])
      })
      attribItems.push(months.join('-') + " " + item.year)
    }
    // note
    var note = null
    if (item.note) {
      note = <span className="note">({item.note})</span>
    }
    // emph
    var emph = null
    if (item.emph) {
      emph = <span className="emph">({item.emph})</span>
    }
    // title w,w/o link
    var title = item.title
    if (item.url) {
      title = <Underlining color={Theme.colors.secondary} hoverColor={Theme.colors.secondary}><a href={item.url}>{item.title}</a></Underlining>
    }
    // pdf, slide links
    const linkItems = [];
    if (item.pdf) {
      linkItems.push(" ")
      linkItems.push(
        <Underlining color={Theme.colors.secondary} hoverColor={Theme.colors.secondary}><a href={lookup_pdf(item.pdf, pdfs)}>[pdf]</a></Underlining>
      )
    }
    if (item.slide) {
      linkItems.push(" ")
      linkItems.push(
        <Underlining color={Theme.colors.secondary} hoverColor={Theme.colors.secondary}><a href={item.slide}>[slide]</a></Underlining>
      )
    }
    sectionItems.push(
      <div className="pub">
        <div className="pub-author">{item.authors}</div>
        <div className="pub-title">{title}</div>
        <div className="pub-attrib">{attribItems.join(", ")} {note} {emph}</div>
        <div className="pub-links">{linkItems}</div>
      </div>
    )
  });
  return <div className="pub-section">{sectionItems}</div>;
}

const Publication = ({ data }) => {
  // header info
  const { body, frontmatter } = data.head.edges[0].node
  // sort sections by position
  const edges = data.publication.edges.sort((a, b) => a.node.position - b.node.position)
  const sections = []
  for (var i = 0; i < edges.length; i++) {
    sections.push(
      sectionHandler(edges[i].node, data.pdf.edges)
    )
  }
  return (
    <Layout splashScreen={false}>
      <SEO title="Publications - Shigemi ISHIDA'S Portfolio" meta={[{ name: 'robots', content: 'noindex' }]} />
      <StyledSection id={frontmatter.title}>
        <StyledContentWrapper>
          <h1>{frontmatter.title}</h1>
          <MDXRenderer>{body}</MDXRenderer>
          {sections}
        </StyledContentWrapper>
      </StyledSection>
    </Layout>
  )
}

Publication.propTypes = {
  data: PropTypes.shape({
    head: PropTypes.shape({
      edges: PropTypes.arrayOf(
        PropTypes.shape({
          node: PropTypes.shape({
            body: PropTypes.string.isRequired,
            frontmatter: PropTypes.object.isRequired,
          }).isRequired,
        }).isRequired
      ).isRequired,
    }).isRequired,
    publication: PropTypes.shape({
      edges: PropTypes.arrayOf(
        PropTypes.shape({
          node: PropTypes.shape({
            title: PropTypes.string.isRequired,
            position: PropTypes.number.isRequired,
            body: PropTypes.arrayOf(
              PropTypes.shape({
                lang: PropTypes.string,
                year: PropTypes.number.isRequired,
                month: PropTypes.string,
                authors: PropTypes.string.isRequired,
                title: PropTypes.string.isRequired,
                in: PropTypes.string.isRequired,
                vol_pp: PropTypes.string,
                url: PropTypes.string,
                pdf: PropTypes.string,
                slide: PropTypes.string,
                note: PropTypes.string,
                emph: PropTypes.string
              }).isRequired
            ).isRequired,
          }).isRequired
        }).isRequired
      ).isRequired,
    }).isRequired,
  }).isRequired,
}

export default Publication

export const pageQuery = graphql`
{
  head: allMdx(filter: { fileAbsolutePath: { regex: "/publication/" } }) {
    edges {
      node {
        body
        frontmatter {
          title
        }
      }
    }
  },
  publication: allPublicationsJson {
    edges {
      node {
        title
        position
        body {
          lang
          year
          month
          authors
          title
          in
          vol_pp
          url
          pdf
          slide
          note
          emph
        }
      }
    }
  },
  pdf: allFile(filter: {absolutePath: {regex: "/pdf/"}}) {
    edges {
      node {
        name
        publicURL
      }
    }
  }
}
`
